import React, { useEffect, useMemo, useRef, useState } from "react";
import "./market-24h-pricerange.css";

function toNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? parseFloat(String(v).replace(/,/g, "")) : Number(v);
  if (Number.isNaN(n) || !Number.isFinite(n)) return null;
  return n;
}

const POINTS = 48;

/* Reference-style: teal/green and rose/red with light area under each segment */
const COLOR = {
  green: "#0d9488",
  red: "#e11d48",
};

/**
 * 24h-like path in [low, high] with wiggles. Starts inside the band (not at the low)
 * so the path crosses the session baseline and produces green + red segments.
 */
function buildSparklineFromLowHigh(low, high, lastPrice) {
  const lo = toNum(low);
  const hi = toNum(high);
  if (lo == null || hi == null) return [];

  const minP = Math.min(lo, hi);
  const maxP = Math.max(lo, hi);
  const span = maxP - minP;
  const last = toNum(lastPrice);

  if (span <= 0) {
    return Array(POINTS).fill(minP);
  }

  const out = [];
  for (let i = 0; i < POINTS; i += 1) {
    const t = i / (POINTS - 1);
    const slow = 0.5 - 0.5 * Math.cos(t * Math.PI);
    const zig = 0.22 * span;
    let v = minP + (0.28 + 0.44 * slow) * span;
    v += zig * Math.sin(t * Math.PI * 7);
    v += 0.45 * zig * Math.sin(t * Math.PI * 15);
    v += 0.3 * zig * Math.sin(t * Math.PI * 23);
    out.push(Math.max(minP, Math.min(maxP, v)));
  }

  if (last != null) {
    const tLast = (last - minP) / span;
    if (tLast >= 0 && tLast <= 1) {
      const k = Math.round(tLast * (POINTS - 1));
      const i = Math.min(POINTS - 2, Math.max(1, k));
      out[i] = Math.max(minP, Math.min(maxP, last));
    }
  }

  return out;
}

function smooth3(arr) {
  if (arr.length < 3) return [...arr];
  const r = [arr[0]];
  for (let i = 1; i < arr.length - 1; i += 1) {
    r.push((arr[i - 1] + arr[i] + arr[i + 1]) / 3);
  }
  r.push(arr[arr.length - 1]);
  return r;
}

function resampleLinear(prices, targetLen) {
  if (prices.length < 2 || targetLen < 2) return [...prices];
  const out = [];
  for (let j = 0; j < targetLen; j += 1) {
    const t = (j / (targetLen - 1)) * (prices.length - 1);
    const i = Math.floor(t);
    const f = t - i;
    const a = prices[Math.min(i, prices.length - 1)];
    const b = prices[Math.min(i + 1, prices.length - 1)];
    out.push(a + f * (b - a));
  }
  return out;
}

function insertOpenCrossings(prices, open) {
  if (prices.length < 2) return [...prices];
  const o = toNum(open);
  if (o == null) return prices;
  const r = [prices[0]];
  for (let i = 0; i < prices.length - 1; i += 1) {
    const a = r[r.length - 1];
    const b = prices[i + 1];
    if (a !== b && (a - o) * (b - o) < 0) {
      if (r[r.length - 1] !== o) r.push(o);
    }
    if (r[r.length - 1] !== b) r.push(b);
  }
  return r;
}

function buildXY(prices, width, height, padding, vPad) {
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  return prices.map((price, index) => ({
    x: (index / (prices.length - 1)) * width,
    y: height - ((price - min) / range) * (height - vPad) - padding,
    price,
  }));
}


const Market24hRangeChart = React.memo(function Market24hRangeChart({ isPositive, low, high, lastPrice, openPrice: openProp }) {
  const canvasRef = useRef(null);
  const [dpr, setDpr] = useState(1);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 767
  );

  const series = useMemo(
    () => buildSparklineFromLowHigh(low, high, lastPrice),
    [low, high, lastPrice]
  );

  const openBaseline = useMemo(() => {
    if (toNum(openProp) != null) return toNum(openProp);
    const s = smooth3(smooth3(series));
    if (s.length > 0) return s[0];
    return null;
  }, [openProp, series]);

  const prepared = useMemo(() => {
    if (series.length < 2) return null;
    let p = resampleLinear(smooth3(smooth3(series)), 140);
    const ob = openBaseline != null ? openBaseline : p[0];
    p = insertOpenCrossings(p, ob);
    return { prices: p, open: ob };
  }, [series, openBaseline]);

  useEffect(() => {
    const onR = () => {
      setIsMobile(window.innerWidth <= 767);
      setDpr(typeof window !== "undefined" && window.devicePixelRatio ? window.devicePixelRatio : 1);
    };
    onR();
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  const cssW = isMobile ? 72 : 98;
  const cssH = isMobile ? 36 : 44;

  useEffect(() => {
    if (!canvasRef.current || !prepared) return;
    const { prices, open } = prepared;
    if (prices.length < 2) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const w = cssW;
    const h = cssH;
    const scale = dpr;
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const pad = isMobile ? 2 : 3;
    const vPad = isMobile ? 7 : 10;
    const pts = buildXY(prices, w, h, pad, vPad);
    const bottomY = h;

    const segUp = (i) => (pts[i].price + pts[i + 1].price) / 2 >= open;

    for (let i = 0; i < pts.length - 1; i += 1) {
      const a = pts[i];
      const b = pts[i + 1];
      const up = segUp(i);
      const grd = ctx.createLinearGradient(0, Math.min(a.y, b.y), 0, bottomY);
      if (up) {
        grd.addColorStop(0, "rgba(13, 148, 136, 0.2)");
        grd.addColorStop(1, "rgba(13, 148, 136, 0)");
      } else {
        grd.addColorStop(0, "rgba(225, 29, 72, 0.18)");
        grd.addColorStop(1, "rgba(225, 29, 72, 0)");
      }
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.lineTo(b.x, bottomY);
      ctx.lineTo(a.x, bottomY);
      ctx.closePath();
      ctx.fillStyle = grd;
      ctx.fill();
    }

    for (let i = 0; i < pts.length - 1; i += 1) {
      const a = pts[i];
      const b = pts[i + 1];
      const up = segUp(i);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.strokeStyle = up ? COLOR.green : COLOR.red;
      ctx.lineWidth = 1.4;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();
    }
  }, [prepared, dpr, isMobile, cssH, cssW]);

  if (series.length < 2) {
    return (
      <img
        src="/images/trade_vector2.svg"
        className="img-fluid"
        alt=""
        style={{
          filter: isPositive ? "none" : "hue-rotate(180deg) saturate(1.5)",
          opacity: 0.5,
        }}
      />
    );
  }

  if (!prepared) return null;

  return (
    <div className="m24h-spark" aria-hidden="true">
      <canvas
        ref={canvasRef}
        className="m24h-spark__canvas"
        style={{
          display: "block",
          maxWidth: "100%",
        }}
      />
    </div>
  );
});

Market24hRangeChart.displayName = "Market24hRangeChart";
export default Market24hRangeChart;
